/**
 * ViewerApp — minimal PlayCanvas runtime for olloport viewer2.
 *
 * Scaffold-level app: boots a PlayCanvas instance (WebGPU first, WebGL2
 * fallback) with the same
 * component systems and resource handlers the editor uses (so any scene the
 * editor produces will load later), wires up a camera, runs the render loop.
 * Scene loading, navigation modes, and the compose pipeline come in later
 * milestones.
 *
 * Design notes:
 *  - We construct AppOptions and component systems explicitly (instead of
 *    using `new pc.Application(...)` which pulls in everything). Mirrors
 *    webpages/editor/src/pc-app.ts and keeps the bundle lean.
 *  - GraphicsDevice creation is async (WebGPU needs it, even when we only
 *    pick WebGL2). Construction therefore goes through the static
 *    `ViewerApp.create()` factory rather than `new ViewerApp(...)`.
 *  - clearColor is opaque black to match the page background; the canvas
 *    itself is faded in by main.ts once the first frame renders.
 */

import * as pc from 'playcanvas';

import { ViewerSplatRenderer } from './splat-render/viewer-splat-renderer';
import { EditorPipeline } from './splat-render/editor-pipeline';
import { registerCameraFrame, registerSceneTarget } from './capture/frame-source';
import { vrDeviceTypes } from './xr/vr-mode';

/**
 * Graphics backend preference, in order. Default is WebGPU with a WebGL2
 * fallback (the engine tries each in turn and takes the first the browser
 * grants). `?gpu=webgl2` pins the old backend and `?gpu=webgpu` insists on the
 * new one — both for side-by-side comparisons; an embed with no URL of its own
 * gets the default.
 */
export function pickDeviceTypes(): string[] {
    let forced: string | null = null;
    try {
        forced = new URL(window.location.href).searchParams.get('gpu');
    } catch { /* no usable URL — default order */ }
    if (forced === 'webgl2') return [pc.DEVICETYPE_WEBGL2];
    if (forced === 'webgpu') return [pc.DEVICETYPE_WEBGPU];
    return [pc.DEVICETYPE_WEBGPU, pc.DEVICETYPE_WEBGL2];
}

/** Shown to the person when the browser has no graphics to give us. On a
 *  Mac this is Chrome after its GPU process crashed: it keeps WebGL and
 *  WebGPU off until it is quit and reopened. */
export const NO_GRAPHICS = 'This browser has turned off 3D graphics (WebGL). Quit the browser completely and open it again.';

function webGl2Available(): boolean {
    try {
        const c = document.createElement('canvas');
        const gl = c.getContext('webgl2');
        if (!gl) return false;
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        return true;
    } catch {
        return false;
    }
}

function assertWebGl2(): void {
    if (!webGl2Available()) throw new Error(NO_GRAPHICS);
}

async function webGpuUsable(): Promise<boolean> {
    const gpu = (navigator as unknown as { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
    if (!gpu) return false;
    try {
        return (await gpu.requestAdapter()) !== null;
    } catch {
        return false;
    }
}

export class ViewerApp {

    readonly canvas: HTMLCanvasElement;

    readonly graphicsDevice: pc.GraphicsDevice;

    readonly app: pc.AppBase;

    readonly cameraEntity: pc.Entity;
    /** 'webgpu' or 'webgl2' — whichever createGraphicsDevice resolved to. */
    readonly deviceType: string;

    /** Post-process frame (HDR intermediate + tonemap/grading/bloom). The
     *  scene loader maps scene.json's `lighting` block onto this. */
    cameraFrame!: pc.CameraFrame;
    /** ?cf=0: render straight to the backbuffer after the reveal (matches SuperSplat). */
    bypassCameraFrame = false;
    /** ?sr=1: draw non-LOD splats with SuperSplat's renderer (pixel parity with the editor). */
    splatRenderer: ViewerSplatRenderer | null = null;
    /** SuperSplat's frame pipeline (16F target + plain blit); null when the CameraFrame chain is in use. */
    editorPipeline: EditorPipeline | null = null;

    /**
     * Leave the direct pipeline for the CameraFrame compose chain (bloom / grading).
     * One-way; mount.ts calls it before the first reveal when the scene's lighting needs it.
     */
    useCameraFrame(): void {
        if (!this.editorPipeline) return;
        this.editorPipeline.destroy();
        this.editorPipeline = null;
        this.cameraFrame.enabled = true;
        this.cameraFrame.update();
        console.log('[viewer2] lighting needs the compose pass: CameraFrame chain on');
    }

    private xrCameraFrameWasOn = false;

    /**
     * VR renders straight into the headset's per-eye views; the 16F target +
     * blit and the CameraFrame chain are single-view, so both step aside.
     */
    enterXrRendering(): void {
        this.editorPipeline?.suspend();
        this.xrCameraFrameWasOn = this.cameraFrame.enabled;
        if (this.xrCameraFrameWasOn) {
            this.cameraFrame.enabled = false;
            this.cameraFrame.update();
        }
    }

    exitXrRendering(): void {
        this.editorPipeline?.resume();
        if (this.xrCameraFrameWasOn) {
            this.cameraFrame.enabled = true;
            this.cameraFrame.update();
        }
    }

    private firstFrameCallbacks: Array<() => void> = [];

    private firstFrameFired = false;

    /**
     * Boot a viewer instance. Async because GraphicsDevice creation is async.
     */
    static async create(canvas: HTMLCanvasElement): Promise<ViewerApp> {
        // WebGPU first, WebGL2 fallback — see pickDeviceTypes(). On WebGPU the
        // engine's gsplat renderer sorts and projects on the GPU every frame
        // (no CPU sort worker, no sort lag while moving); WebGL2 keeps every
        // older browser working. antialias:false because gsplat uses its own
        // per-pixel Gaussian AA; MSAA on the backbuffer would just cost fillrate.
        //
        // displayFormat: HDR (RGBA16F backbuffer) is CRITICAL for splat quality.
        // With the default LDR (RGBA8) backbuffer, accumulating hundreds of
        // partially-transparent Gaussians per pixel quantises so hard that
        // individual splat footprints become visible — the user sees ugly
        // "splat borders" instead of a smooth blended surface. 16F gives the
        // alpha math the precision it needs to blend cleanly.
        // A VR-capable browser that can't bind WebGPU to XR boots on WebGL2,
        // so "Enter VR" works (xr/vr-mode.ts). ?gpu= still wins.
        const forced = new URL(window.location.href).searchParams.get('gpu');
        const deviceTypes = (!forced && await vrDeviceTypes()) || pickDeviceTypes();
        // Guard before the engine's fallback chain: when WebGPU fails and
        // the browser then refuses a WebGL2 context, the engine throws
        // "WebGL not supported" synchronously inside its own catch handler,
        // the error is unhandled and createGraphicsDevice never settles —
        // the viewer hangs on a black screen (Chrome on a MacBook Air after
        // its GPU process had crashed, 13/10/2026). With WebGL2 confirmed
        // here the chain can only reach a device; without it, fail at once
        // with a message the page can show.
        if (!deviceTypes.includes(pc.DEVICETYPE_WEBGPU) || !await webGpuUsable()) {
            assertWebGl2();
        } else if (!webGl2Available()) {
            // WebGPU looks usable, WebGL2 does not: never let the engine fall
            // back into the throw above.
            const graphicsDevice = await pc.createGraphicsDevice(canvas, {
                deviceTypes: [pc.DEVICETYPE_WEBGPU, pc.DEVICETYPE_NULL],
                displayFormat: pc.DISPLAYFORMAT_HDR,
                antialias: false,
                powerPreference: 'high-performance'
            }) as pc.GraphicsDevice;
            if ((graphicsDevice.deviceType as string) === pc.DEVICETYPE_NULL) throw new Error(NO_GRAPHICS);
            return new ViewerApp(canvas, graphicsDevice);
        }
        const graphicsDevice = await pc.createGraphicsDevice(canvas, {
            deviceTypes,
            displayFormat: pc.DISPLAYFORMAT_HDR,
            antialias: false,
            powerPreference: 'high-performance'
        }) as pc.GraphicsDevice;
        if ((graphicsDevice.deviceType as string) === pc.DEVICETYPE_NULL) throw new Error(NO_GRAPHICS);

        return new ViewerApp(canvas, graphicsDevice);
    }

    private constructor(canvas: HTMLCanvasElement, graphicsDevice: pc.GraphicsDevice) {
        this.canvas = canvas;
        this.graphicsDevice = graphicsDevice;
        this.deviceType = graphicsDevice.deviceType;

        // ── App ───────────────────────────────────────────────────────────
        const appOptions = new pc.AppOptions();
        appOptions.graphicsDevice = graphicsDevice;
        appOptions.keyboard = new pc.Keyboard(window);
        appOptions.mouse = new pc.Mouse(canvas);
        if ('ontouchstart' in window) {
            appOptions.touch = new pc.TouchDevice(canvas);
        }

        // Component systems — same set the editor registers, minus editor-only
        // gizmos. GSplatComponentSystem is the critical one.
        appOptions.componentSystems = [
            pc.RenderComponentSystem,
            pc.CameraComponentSystem,
            pc.LightComponentSystem,
            pc.GSplatComponentSystem,
            // ScriptComponentSystem — needed to attach the engine's official
            // GsplatReveal* shader-effect scripts (radial/rain/grid) so splat
            // reveal effects authored in the editor play in the viewer.
            pc.ScriptComponentSystem
        ] as unknown as typeof pc.ComponentSystem[];

        // Resource handlers — load .glb (mesh), textures, cubemaps,
        // .ply/.spz/.sog (splat) assets, and .js scripts.
        appOptions.resourceHandlers = [
            pc.RenderHandler,
            pc.TextureHandler,
            pc.CubemapHandler,
            pc.ContainerHandler,
            pc.GSplatHandler,
            pc.ScriptHandler
        ] as unknown as typeof pc.ResourceHandler[];
        appOptions.xr = pc.XrManager;

        this.app = new pc.AppBase(canvas);
        this.app.init(appOptions);
        if (import.meta.env.DEV) {
            // Dev-only handles for in-page experiments (headless probes, devtools).
            const w = window as unknown as Record<string, unknown>;
            w.__pcapp = this.app;
            w.__pc = pc;
            w.__viewerApp = this;
        }

        // ── Scene defaults ────────────────────────────────────────────────
        this.app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
        this.app.setCanvasResolution(pc.RESOLUTION_AUTO);
        this.app.scene.ambientLight = new pc.Color(0, 0, 0);

        // ── GSplat scene-wide config (canonical from PC lod-streaming examples) ─
        // These knobs apply to ALL splats in the scene; per-entity overrides
        // (lodBaseDistance, lodMultiplier) come from the scene loader later.
        const gs = this.app.scene.gsplat;
        gs.renderer = pc.GSPLAT_RENDERER_AUTO;
        gs.alphaClipForward = 1 / 255;
        // Loose culling thresholds for now — the adaptive controller (milestone 9)
        // will tune these per-device. With minPixelSize=2 + minContribution=3
        // the chunk evaluator was evicting everything in scenes wider than a
        // few metres before any splat ever made it to the frame.
        gs.minPixelSize = 1;
        gs.minContribution = 1;
        gs.radialSorting = true;
        gs.lodUpdateAngle = 90;
        gs.lodBehindPenalty = 0;                 // don't penalise behind-cam during init
        gs.lodUpdateDistance = 0.5;
        gs.lodUnderfillLimit = 5;
        gs.colorUpdateAngle = 10;                // SH recompute threshold
        // dataFormat: COMPACT halves work-buffer memory but only works for some
        // asset formats. LARGE is the safe default that works for everything
        // including the editor's splat-transform SOG-style bundles. The adaptive
        // controller (milestone 9) can drop to COMPACT on low-mem devices.
        gs.dataFormat = pc.GSPLATDATA_LARGE;
        // Splat budget — defaults from examples. Adaptive controller will
        // mutate this at runtime when FPS drops (milestone 9).
        gs.splatBudget = pc.platform.mobile ? 1_000_000 : 8_000_000;
        // dev knob: ?gs.<param>=<value> overrides any GSplatParams field before the scene loads
        for (const [k, v] of new URL(window.location.href).searchParams) {
            if (!k.startsWith('gs.')) continue;
            const num = Number(v);
            (gs as unknown as Record<string, unknown>)[k.slice(3)] = v === 'true' ? true : v === 'false' ? false : Number.isFinite(num) && v.trim() !== '' ? num : v;
            console.log('[viewer2] gsplat override', k.slice(3), '=', v);
        }

        // ── Camera ────────────────────────────────────────────────────────
        // Placeholder pose; the scene loader (milestone 4) overwrites this
        // from scene.camera.initial (or computes from scene bounds if absent).
        this.cameraEntity = new pc.Entity('viewer-camera');
        this.cameraEntity.addComponent('camera', {
            clearColor: new pc.Color(7 / 255, 7 / 255, 7 / 255, 1),  // matches --bg
            fov: 60,
            nearClip: 0.01,
            farClip: 1000,
            projection: pc.PROJECTION_PERSPECTIVE
        });
        this.cameraEntity.setPosition(0, 1.6, 4);
        this.cameraEntity.lookAt(0, 0, 0);
        this.app.root.addChild(this.cameraEntity);

        // ── CameraFrame: HDR intermediate + tonemap ──────────────────────
        // Splats accumulate hundreds of partial-alpha samples per pixel. If
        // that math runs against an LDR (RGBA8) target, the rounding shows
        // up as visible splat borders. pc.CameraFrame wires the camera to
        // render into an RGBA16F intermediate target and then tonemaps to
        // the backbuffer — same approach the editor uses (and the official
        // lod-streaming examples). The borders disappear; this is the knob
        // the user was asking for as "high-precision rendering mode".
        const cameraFrame = new pc.CameraFrame(this.app, this.cameraEntity.camera as pc.CameraComponent);
        cameraFrame.rendering.renderFormats = [
            pc.PIXELFORMAT_RGBA16F,
            pc.PIXELFORMAT_RGBA32F,
            pc.PIXELFORMAT_111110F
        ];
        cameraFrame.rendering.samples = 1;
        cameraFrame.rendering.toneMapping = pc.TONEMAP_LINEAR;
        cameraFrame.update();
        this.cameraFrame = cameraFrame;
        // ?cf=0: bypass the CameraFrame post-process chain and render straight to the backbuffer like
        // SuperSplat. Measured on the Roche corridor: the chain lifts brightness ~9% and softens the
        // image; without it the frame matches the editor (brightness 123.5 vs 122.2, sharpness 14.6 vs 13.7).
        // The boot paint probe reads the CameraFrame's scene texture (WebGPU has no readable
        // canvas), so the chain stays on until the reveal; mount.ts drops it after.
        this.bypassCameraFrame = new URL(window.location.href).searchParams.get('cf') === '0';
        // Lets the paint probe and the screenshot capture read the scene
        // target on WebGPU (see capture/frame-source.ts).
        registerCameraFrame(this.app, cameraFrame);
        // Default: SuperSplat's frame pipeline (16-bit target + plain blit). The
        // CameraFrame compose lifted brightness ~9 % and softened the image, and
        // without it the 8-bit backbuffer mottled every flat surface; this path
        // matches the editor pixel for pixel (documentation/viewer/PLAYCANVAS_UPGRADE.md).
        // Scenes whose lighting needs bloom / contrast / colour enhance switch back
        // to the CameraFrame in mount.ts (useCameraFrame). ?ep=0 forces the old chain.
        // the direct pipeline needs a renderable half-float target (every WebGPU device; WebGL2 with EXT_color_buffer_float)
        const halfFloatTarget = this.app.graphicsDevice.isWebGPU ||
            (this.app.graphicsDevice as unknown as { textureHalfFloatRenderable?: boolean }).textureHalfFloatRenderable === true;
        if (halfFloatTarget && new URL(window.location.href).searchParams.get('ep') !== '0') {
            cameraFrame.enabled = false;
            this.bypassCameraFrame = false;
            this.editorPipeline = new EditorPipeline(this.app, this.cameraEntity);
            registerSceneTarget(this.app, () => {
                const t = this.editorPipeline?.sceneTarget;
                return t ? { ...t, encoded: true } : null;
            });
        }
        if (new URL(window.location.href).searchParams.get('sr') === '1') {
            this.splatRenderer = new ViewerSplatRenderer(this.app, this.cameraEntity);
            (this.app as unknown as { splatRenderer?: ViewerSplatRenderer }).splatRenderer = this.splatRenderer;
            console.log('[viewer2] SuperSplat renderer on (?sr=1)');
        }

        // ── Window sizing ─────────────────────────────────────────────────
        const resize = () => this.app.resizeCanvas();
        window.addEventListener('resize', resize);
        resize();

        // First-frame fan-out, fired by the engine after each render pass.
        this.app.on('postrender', () => {
            if (!this.firstFrameFired) {
                this.firstFrameFired = true;
                for (const cb of this.firstFrameCallbacks) cb();
                this.firstFrameCallbacks = [];
            }
        });
    }

    /** Begin the render loop. Idempotent — calling twice is a no-op. */
    start(): void {
        this.app.start();
    }

    /** Register a callback fired once, after the first frame is rendered. */
    onFirstFrame(cb: () => void): void {
        if (this.firstFrameFired) {
            cb();
            return;
        }
        this.firstFrameCallbacks.push(cb);
    }

    /** Tear down — releases GPU resources. Useful when embedded as a component. */
    destroy(): void {
        this.app.destroy();
    }
}
